/**
 * Iter XX.243b · Public Cricket Identity editor.
 *
 * The single, minimal 15-20 second flow to become discoverable:
 *   Name → Role chip → Team (optional) → City (optional) → Bio →
 *   two toggles (Discoverable / Location public) → Save.
 *
 * Renders as either a modal-style panel (used by Pulse onboarding
 * banner + "Edit my cricket profile") or embedded on `/cricket`.
 *
 * Privacy: the component consumes ONLY the compact editable-profile
 * shape from `/api/pulse/profile/me/editable`. It cannot render
 * fields the API didn't surface — email / phone / private cricket-
 * graph data simply don't exist on the client.
 */
import React, { useEffect, useState } from "react";
import {
  fetchPulseEditableProfile, savePulsePublicProfile,
} from "../lib/pulse";

const ROLES = [
  "batter", "fast bowler", "spin bowler", "wicketkeeper", "all-rounder",
];

export default function PulseProfileEditor({ onSaved, onCancel }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      const data = await fetchPulseEditableProfile();
      setP(data || {
        display_name: "", public_role: null, public_team: "",
        public_bio: "", public_location: "",
        discoverable: false, public_location_optin: false,
      });
    })();
  }, []);

  if (!p) return (
    <div data-testid="pulse-profile-editor-loading" className="text-neutral-500 text-[13px] p-4">
      Loading…
    </div>
  );

  const set = (k, v) => setP((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    const res = await savePulsePublicProfile({
      display_name:          (p.display_name || "").trim() || undefined,
      public_role:           p.public_role || undefined,
      public_team:           (p.public_team || "").trim() || null,
      public_location:       (p.public_location || "").trim() || null,
      public_bio:            (p.public_bio || "").trim() || null,
      discoverable:          !!p.discoverable,
      public_location_optin: !!p.public_location_optin,
    });
    setBusy(false);
    if (res?.error) { setErr(res.error); return; }
    onSaved && onSaved(res);
  };

  return (
    <div
      data-testid="pulse-profile-editor"
      className="rounded-3xl border border-neutral-800 bg-neutral-900/70 p-5 my-4"
    >
      <div className="text-[11px] uppercase tracking-widest text-emerald-500 mb-3">
        Edit my cricket profile
      </div>

      <label className="block mb-3">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Display name</div>
        <input
          data-testid="pulse-editor-name"
          value={p.display_name || ""}
          maxLength={60}
          onChange={(e) => set("display_name", e.target.value)}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800"
        />
      </label>

      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Cricket role</div>
        <div className="flex gap-2 flex-wrap">
          {ROLES.map((r) => (
            <button
              key={r}
              data-testid={`pulse-editor-role-${r.replace(/\s+/g, "-")}`}
              onClick={() => set("public_role", r)}
              className={
                "px-3 py-1.5 rounded-full text-[13px] border transition-colors capitalize " +
                (p.public_role === r
                  ? "bg-emerald-500 text-neutral-950 border-emerald-500"
                  : "border-neutral-800 text-neutral-300 hover:bg-neutral-800")
              }
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <label className="block mb-3">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Team or club (optional)</div>
        <input
          data-testid="pulse-editor-team"
          value={p.public_team || ""}
          maxLength={80}
          onChange={(e) => set("public_team", e.target.value)}
          placeholder="e.g. Sunbeam School XI"
          className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800"
        />
      </label>

      <label className="block mb-3">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">City or region (optional)</div>
        <input
          data-testid="pulse-editor-location"
          value={p.public_location || ""}
          maxLength={80}
          onChange={(e) => set("public_location", e.target.value)}
          placeholder="e.g. Mumbai"
          className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800"
        />
      </label>

      <label className="block mb-4">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Short cricket bio (optional)</div>
        <textarea
          data-testid="pulse-editor-bio"
          rows={2}
          value={p.public_bio || ""}
          maxLength={240}
          onChange={(e) => set("public_bio", e.target.value)}
          placeholder="e.g. 16, love the pull shot"
          className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800 resize-none"
        />
      </label>

      <label className="flex items-start gap-3 p-3 rounded-2xl border border-neutral-800 bg-neutral-950/60 mb-3 cursor-pointer">
        <input
          data-testid="pulse-editor-discoverable"
          type="checkbox"
          checked={!!p.discoverable}
          onChange={(e) => set("discoverable", e.target.checked)}
          className="mt-1 accent-emerald-500"
        />
        <div>
          <div className="text-[14px] text-neutral-100">Allow other cricketers to find me on Gully</div>
          <div className="text-[12px] text-neutral-500 mt-0.5">
            You'll appear in search and suggestions. You can turn this off anytime.
          </div>
        </div>
      </label>

      <label className="flex items-start gap-3 p-3 rounded-2xl border border-neutral-800 bg-neutral-950/60 mb-4 cursor-pointer">
        <input
          data-testid="pulse-editor-location-optin"
          type="checkbox"
          checked={!!p.public_location_optin}
          onChange={(e) => set("public_location_optin", e.target.checked)}
          className="mt-1 accent-emerald-500"
        />
        <div>
          <div className="text-[14px] text-neutral-100">Show my city on my profile</div>
          <div className="text-[12px] text-neutral-500 mt-0.5">
            Off by default. Your city is never shown unless you turn this on.
          </div>
        </div>
      </label>

      {err && (
        <div data-testid="pulse-editor-error" className="text-[13px] text-rose-400 mb-3">
          {err}
        </div>
      )}

      <div className="flex gap-3">
        <button
          data-testid="pulse-editor-save"
          onClick={save}
          disabled={busy}
          className="flex-1 py-3 rounded-full bg-emerald-500 text-neutral-950 font-medium hover:bg-emerald-400 transition-colors disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save profile"}
        </button>
        <button
          data-testid="pulse-editor-cancel"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-3 rounded-full border border-neutral-800 text-neutral-300 hover:bg-neutral-800 transition-colors"
        >
          Cancel
        </button>
      </div>

      <p className="text-[12px] text-neutral-500 mt-4 leading-relaxed">
        Only what you enter here appears publicly. Your AI conversations, private goals and cricket graph stay private.
      </p>
    </div>
  );
}
